/**
 * The series palette for charts (SRS #56).
 *
 * recharts takes its colours as props rather than from CSS, so a chart cannot
 * read `var(--ds-brand-blue)` the way the rest of a screen does. That is why
 * the dashboard kept drawing indigo bars next to an orange donut after KN's
 * brand was applied: the donut used a token, the bars were a literal.
 *
 * These are therefore the one place a chart colour is written down. Change a
 * value here and every chart follows; write a literal in a chart and it will
 * be the only thing on the page that did not move the next time the brand does.
 *
 * SERIES is ordered so the first two carry KN's own colours — most charts plot
 * one or two things — and the rest stay distinguishable from them and from
 * each other, including for the commonest colour-vision deficiencies.
 */

export const BRAND_ORANGE = '#ff8200';
export const BRAND_ORANGE_DEEP = '#d96e00';
export const BRAND_ORANGE_LIGHT = '#ff9a33';
export const BRAND_BLUE = '#0756b8';
export const BRAND_BLUE_SOFT = '#4f8adf';
export const BRAND_NAVY = '#082a59';

/* The same four values the CSS tokens carry. A component that styles inline —
   recharts props, an onMouseEnter that sets style.color — cannot read a token,
   so it reads these. */

/** Categorical series, in the order a chart should take them. */
export const SERIES = [
  BRAND_ORANGE,
  BRAND_BLUE,
  '#15b79e',   // teal
  BRAND_BLUE_SOFT,
  '#7a5af8',   // violet
  '#f63d68',   // rose
  '#98a2b3',   // grey, for an "other" bucket
] as const;

/**
 * Status colours, which are not brand colours and must not become them: these
 * say whether an obligation is met, pending or lapsed, and a reader has to be
 * able to tell them apart without reading the legend.
 */
export const STATUS = {
  enforced: '#12b76a',
  pending: BRAND_ORANGE,
  inactive: '#98a2b3',
} as const;

/** Axis, grid and label colours, which differ by ground. */
export const axis = (isDark: boolean) => ({
  text: isDark ? '#f2f4f7' : BRAND_NAVY,
  muted: isDark ? '#98a2b3' : '#526980',
  grid: isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0',
});
